import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { ScrollArea } from '@/components/ui/scroll-area';
import { toast } from 'sonner';
import { 
  Clock, Play, Pause, RefreshCw, CheckCircle, XCircle, 
  Calendar, Settings, Zap, AlertTriangle, Activity, Timer
} from 'lucide-react';
import api from '@/lib/api';

export default function DnaPayScheduler() {
  const [loading, setLoading] = useState(true);
  const [jobs, setJobs] = useState([]);
  const [execucoes, setExecucoes] = useState([]);
  const [status, setStatus] = useState(null);
  const [executando, setExecutando] = useState(null);
  const [showConfig, setShowConfig] = useState(false);
  const [selectedJob, setSelectedJob] = useState(null);
  const [horarioConfig, setHorarioConfig] = useState({ horario: '08:00', dias: [0, 1, 2, 3, 4] });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [jobsRes, execRes, statusRes] = await Promise.all([
        api.get('/dnapay/scheduler/jobs'),
        api.get('/dnapay/scheduler/execucoes?limit=20'),
        api.get('/dnapay/scheduler/status')
      ]);
      
      setJobs(jobsRes.data?.jobs || []);
      setExecucoes(execRes.data?.execucoes || []);
      setStatus(statusRes.data);
    } catch (error) {
      toast.error('Erro ao carregar dados do agendador');
    } finally {
      setLoading(false);
    }
  };

  const executarJob = async (jobTipo) => {
    setExecutando(jobTipo);
    try {
      const res = await api.post(`/dnapay/scheduler/jobs/${jobTipo}/executar`);
      toast.success(`Job executado com sucesso!`);
      loadData();
    } catch (error) {
      toast.error('Erro ao executar job');
    } finally {
      setExecutando(null);
    }
  };

  const toggleJob = async (jobTipo) => {
    try {
      await api.post(`/dnapay/scheduler/jobs/${jobTipo}/toggle`);
      toast.success('Status atualizado');
      loadData();
    } catch (error) {
      toast.error('Erro ao atualizar status');
    }
  };

  const executarTodos = async () => {
    try {
      const res = await api.post('/dnapay/scheduler/executar-todos');
      toast.success(`${res.data?.executados || 0} jobs executados`);
      loadData();
    } catch (error) {
      toast.error('Erro ao executar jobs');
    }
  };

  const atualizarHorario = async () => {
    if (!selectedJob) return;
    try {
      await api.put(`/dnapay/scheduler/jobs/${selectedJob.tipo}/horario`, null, {
        params: {
          horario: horarioConfig.horario,
          dias_semana: horarioConfig.dias
        }
      });
      toast.success('Horário atualizado');
      setShowConfig(false);
      loadData();
    } catch (error) {
      toast.error('Erro ao atualizar horário');
    }
  };

  const diasSemana = [
    { value: 0, label: 'Seg' },
    { value: 1, label: 'Ter' },
    { value: 2, label: 'Qua' },
    { value: 3, label: 'Qui' },
    { value: 4, label: 'Sex' },
    { value: 5, label: 'Sáb' },
    { value: 6, label: 'Dom' }
  ];

  const getJobIcon = (tipo) => {
    switch (tipo) {
      case 'regua_cobranca': return <Zap className="w-4 h-4" />;
      case 'regua_mensagens': return <Activity className="w-4 h-4" />;
      case 'processar_fila': return <RefreshCw className="w-4 h-4" />;
      case 'atualizar_encargos': return <Timer className="w-4 h-4" />;
      case 'gerar_mensalidades': return <Calendar className="w-4 h-4" />;
      case 'relatorio_diario': return <AlertTriangle className="w-4 h-4" />;
      default: return <Clock className="w-4 h-4" />;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <RefreshCw className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="dnapay-scheduler">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold">Agendador de Tarefas</h1>
          <p className="text-muted-foreground">Gerencie jobs automáticos do DNA Pay</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={loadData} data-testid="refresh-btn">
            <RefreshCw className="w-4 h-4 mr-2" />
            Atualizar
          </Button>
          <Button onClick={executarTodos} data-testid="run-all-btn">
            <Play className="w-4 h-4 mr-2" />
            Executar Todos
          </Button>
        </div>
      </div>

      {/* Status Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Jobs Ativos</p>
                <p className="text-2xl font-bold">{status?.jobs_ativos || 0}</p>
              </div>
              <CheckCircle className="w-8 h-8 text-green-500" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total de Jobs</p>
                <p className="text-2xl font-bold">{status?.total_jobs || 0}</p>
              </div>
              <Clock className="w-8 h-8 text-blue-500" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Execuções Hoje</p>
                <p className="text-2xl font-bold">{status?.execucoes_hoje || 0}</p>
              </div>
              <Activity className="w-8 h-8 text-purple-500" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Falhas Hoje</p>
                <p className="text-2xl font-bold">{status?.falhas_hoje || 0}</p>
              </div>
              <XCircle className={`w-8 h-8 ${status?.falhas_hoje > 0 ? 'text-red-500' : 'text-gray-300'}`} />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Jobs Table */}
      <Card>
        <CardHeader>
          <CardTitle>Jobs Configurados</CardTitle>
          <CardDescription>Tarefas automáticas agendadas para execução</CardDescription>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Job</TableHead>
                <TableHead>Horário</TableHead>
                <TableHead>Dias</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Última Execução</TableHead>
                <TableHead className="text-right">Ações</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {jobs.map((job) => (
                <TableRow key={job.tipo} data-testid={`job-row-${job.tipo}`}>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      {getJobIcon(job.tipo)}
                      <div>
                        <p className="font-medium">{job.nome}</p>
                        <p className="text-xs text-muted-foreground">{job.descricao}</p>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>{job.horario_execucao || '08:00'}</TableCell>
                  <TableCell>
                    <div className="flex gap-1">
                      {(job.dias_semana || [0, 1, 2, 3, 4]).map(d => (
                        <Badge key={d} variant="outline" className="text-xs">
                          {diasSemana.find(ds => ds.value === d)?.label}
                        </Badge>
                      ))}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <Switch
                        checked={job.ativo !== false}
                        onCheckedChange={() => toggleJob(job.tipo)}
                      />
                      <Badge variant={job.ativo !== false ? 'default' : 'secondary'}>
                        {job.ativo !== false ? 'Ativo' : 'Pausado'}
                      </Badge>
                    </div>
                  </TableCell>
                  <TableCell>
                    {job.ultima_execucao ? (
                      <span className="text-sm">
                        {new Date(job.ultima_execucao).toLocaleString('pt-BR')}
                      </span>
                    ) : (
                      <span className="text-muted-foreground text-sm">Nunca</span>
                    )}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          setSelectedJob(job);
                          setHorarioConfig({
                            horario: job.horario_execucao || '08:00',
                            dias: job.dias_semana || [0, 1, 2, 3, 4]
                          });
                          setShowConfig(true);
                        }}
                      >
                        <Settings className="w-4 h-4" />
                      </Button>
                      <Button
                        size="sm"
                        onClick={() => executarJob(job.tipo)}
                        disabled={executando === job.tipo}
                      >
                        {executando === job.tipo ? (
                          <RefreshCw className="w-4 h-4 animate-spin" />
                        ) : (
                          <Play className="w-4 h-4" />
                        )}
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Recent Executions */}
      <Card>
        <CardHeader>
          <CardTitle>Execuções Recentes</CardTitle>
          <CardDescription>Histórico das últimas execuções de jobs</CardDescription>
        </CardHeader>
        <CardContent>
          <ScrollArea className="h-[300px]">
            <div className="space-y-3">
              {execucoes.length === 0 ? (
                <Alert>
                  <AlertDescription>Nenhuma execução registrada ainda.</AlertDescription>
                </Alert>
              ) : (
                execucoes.map((exec) => (
                  <div key={exec.id} className="flex items-center justify-between p-3 border rounded-lg">
                    <div className="flex items-center gap-3">
                      {exec.status === 'concluido' ? (
                        <CheckCircle className="w-5 h-5 text-green-500" />
                      ) : (
                        <XCircle className="w-5 h-5 text-red-500" />
                      )}
                      <div>
                        <p className="font-medium">{exec.job_tipo}</p>
                        <p className="text-xs text-muted-foreground">
                          {new Date(exec.inicio).toLocaleString('pt-BR')}
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <Badge variant={exec.status === 'concluido' ? 'default' : 'destructive'}>
                        {exec.status}
                      </Badge>
                      {exec.duracao_segundos && (
                        <p className="text-xs text-muted-foreground mt-1">
                          {exec.duracao_segundos.toFixed(2)}s
                        </p>
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>
          </ScrollArea>
        </CardContent>
      </Card>

      {/* Config Dialog */}
      <Dialog open={showConfig} onOpenChange={setShowConfig}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Configurar Horário</DialogTitle>
            <DialogDescription>
              {selectedJob?.nome}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <Label>Horário de Execução</Label>
              <Input
                type="time"
                value={horarioConfig.horario}
                onChange={(e) => setHorarioConfig({ ...horarioConfig, horario: e.target.value })}
              />
            </div>

            <div>
              <Label>Dias da Semana</Label>
              <div className="flex flex-wrap gap-2 mt-2">
                {diasSemana.map((dia) => (
                  <Button
                    key={dia.value}
                    size="sm"
                    variant={horarioConfig.dias.includes(dia.value) ? 'default' : 'outline'}
                    onClick={() => {
                      const novosDias = horarioConfig.dias.includes(dia.value)
                        ? horarioConfig.dias.filter(d => d !== dia.value)
                        : [...horarioConfig.dias, dia.value];
                      setHorarioConfig({ ...horarioConfig, dias: novosDias });
                    }}
                  >
                    {dia.label}
                  </Button>
                ))}
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowConfig(false)}>
              Cancelar
            </Button>
            <Button onClick={atualizarHorario}>
              Salvar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
